import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronDown, Settings, Plus, Building } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store/rootReducer';
import {
  selectAllWorkspaces,
  selectGetAllWorkspacesLoading,
  selectSwitchWorkspaceLoading,
  selectSwitchWorkspaceError,
  selectSwitchWorkspaceSuccess,
  selectCreateWorkspaceSuccess,
} from '../../store/workspace/workspace.selector';
import {
  getAllWorkspacesRequest,
  switchWorkspaceRequest,
  clearWorkspaceState,
} from '../../store/workspace/workspace.action';
import { useToast } from '../../contexts/ToastContext';
import CreateWorkspaceModal from './CreateWorkspaceModal';

interface WorkspaceDropdownProps {
  currentWorkspace: any;
}

const WorkspaceDropdown: React.FC<WorkspaceDropdownProps> = ({
  currentWorkspace,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const workspaceDropdownRef = useRef<HTMLDivElement | null>(null);

  const [workspaceDropdownOpen, setWorkspaceDropdownOpen] =
    useState<boolean>(false);
  const [createWorkspaceModalOpen, setCreateWorkspaceModalOpen] =
    useState<boolean>(false);

  // Redux state
  const allWorkspaces = useSelector((state: RootState) =>
    selectAllWorkspaces(state)
  );
  const getAllWorkspacesLoading = useSelector((state: RootState) =>
    selectGetAllWorkspacesLoading(state)
  );
  const switchWorkspaceLoading = useSelector((state: RootState) =>
    selectSwitchWorkspaceLoading(state)
  );
  const switchWorkspaceError = useSelector((state: RootState) =>
    selectSwitchWorkspaceError(state)
  );
  const switchWorkspaceSuccess = useSelector((state: RootState) =>
    selectSwitchWorkspaceSuccess(state)
  );
  const createWorkspaceSuccess = useSelector((state: RootState) =>
    selectCreateWorkspaceSuccess(state)
  );

  useEffect(() => {
    if (allWorkspaces.length === 0 && !getAllWorkspacesLoading) {
      dispatch(getAllWorkspacesRequest());
    }
  }, [allWorkspaces.length, getAllWorkspacesLoading, dispatch]);

  useEffect(() => {
    if (switchWorkspaceSuccess) {
      showToast({
        type: 'success',
        title: 'Success',
        message: 'Workspace switched successfully!',
      });
      dispatch(clearWorkspaceState());
      navigate('/dashboard');
    }
  }, [switchWorkspaceSuccess, showToast, navigate, dispatch]);

  useEffect(() => {
    if (switchWorkspaceError) {
      showToast({
        type: 'error',
        title: 'Workspace Switch Error',
        message: `Failed to switch workspace: ${switchWorkspaceError}`,
      });
      dispatch(clearWorkspaceState());
    }
  }, [switchWorkspaceError, showToast, dispatch]);

  useEffect(() => {
    if (createWorkspaceSuccess) {
      dispatch(getAllWorkspacesRequest());
      dispatch(clearWorkspaceState());
    }
  }, [createWorkspaceSuccess, dispatch]);

  const handleWorkspaceSwitch = (workspaceId: string) => {
    dispatch(switchWorkspaceRequest(workspaceId));
    setWorkspaceDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        workspaceDropdownRef.current &&
        !workspaceDropdownRef.current.contains(event.target as Node)
      ) {
        setWorkspaceDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCreateWorkspace = () => {
    setWorkspaceDropdownOpen(false);
    setCreateWorkspaceModalOpen(true);
  };

  return (
    <>
      {workspaceDropdownOpen && (
        <div
          className="fixed inset-0 bg-foreground/20 backdrop-blur-sm z-40"
          onClick={() => setWorkspaceDropdownOpen(false)}
        />
      )}

      <div className="relative z-50" ref={workspaceDropdownRef}>
        <button
          onClick={() => setWorkspaceDropdownOpen(!workspaceDropdownOpen)}
          className={`flex items-center justify-between gap-2 w-full p-1.5 rounded-lg transition-all duration-200 focus:outline-none ${
            workspaceDropdownOpen
              ? 'bg-sidebar-accent'
              : 'hover:bg-sidebar-accent/50'
          }`}
        >
          <div className="flex items-center gap-2 min-w-0">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-sidebar-foreground truncate">
                {currentWorkspace?.name || 'Select Workspace'}
              </p>
            </div>
          </div>
          <ChevronDown
            className={`w-3.5 h-3.5 text-sidebar-muted transition-transform duration-200 flex-shrink-0 ${
              workspaceDropdownOpen ? 'rotate-180' : ''
            }`}
          />
        </button>

        {/* Workspace Dropdown Menu */}
        {workspaceDropdownOpen && (
          <div
            className={`absolute left-0 top-full mt-2 w-80 bg-popover rounded-xl shadow-2xl border border-border z-50 animate-in slide-in-from-top-2 duration-200 transform transition-all`}
          >
            <div className="px-4 py-3 border-b border-border rounded-t-xl bg-muted">
              <p className="text-sm font-semibold text-popover-foreground">
                Switch Workspace
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Choose a workspace to work in
              </p>
            </div>

            <div className="max-h-64 overflow-y-auto">
              {getAllWorkspacesLoading ? (
                <div className="px-4 py-6 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-3"></div>
                  <p className="text-sm text-muted-foreground">
                    Loading workspaces...
                  </p>
                </div>
              ) : allWorkspaces.length > 0 ? (
                allWorkspaces.map((workspace) => (
                  <button
                    key={workspace._id}
                    onClick={() => handleWorkspaceSwitch(workspace._id!)}
                    disabled={switchWorkspaceLoading}
                    className={`w-full text-left px-4 py-3 hover:bg-muted transition-all duration-200 group ${
                      currentWorkspace?._id === workspace._id
                        ? 'bg-primary/10 border-r-3 border-primary shadow-sm'
                        : ''
                    } ${switchWorkspaceLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                          currentWorkspace?._id === workspace._id
                            ? 'bg-primary shadow-md'
                            : 'bg-muted group-hover:bg-accent'
                        }`}
                      >
                        <span
                          className={`font-semibold text-sm ${
                            currentWorkspace?._id === workspace._id
                              ? 'text-primary-foreground'
                              : 'text-muted-foreground'
                          }`}
                        >
                          {workspace.name?.charAt(0)?.toUpperCase() || 'W'}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-sm font-medium truncate ${
                            currentWorkspace?._id === workspace._id
                              ? 'text-primary'
                              : 'text-popover-foreground'
                          }`}
                        >
                          {workspace.name}
                        </p>
                        {currentWorkspace?._id === workspace._id && (
                          <p className="text-xs text-primary mt-0.5 font-medium">
                            Current workspace
                          </p>
                        )}
                      </div>
                      {currentWorkspace?._id === workspace._id && (
                        <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 shadow-sm"></div>
                      )}
                    </div>
                  </button>
                ))
              ) : (
                <div className="px-4 py-6 text-center">
                  <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Building className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    No workspaces found
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Create a workspace to get started
                  </p>
                </div>
              )}
            </div>

            {/* Workspace Actions */}
            <div className="border-t border-border px-2 py-2 bg-muted/50 rounded-b-xl">
              <button
                onClick={handleCreateWorkspace}
                className="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-popover-foreground hover:bg-muted rounded-lg transition-all duration-200 border border-transparent hover:border-border"
              >
                <div className="w-6 h-6 bg-primary/10 rounded-md flex items-center justify-center">
                  <Plus className="w-3.5 h-3.5 text-primary" />
                </div>
                Create New Workspace
              </button>

              <div className="relative mt-1">
                <button
                  onClick={() => {
                    setWorkspaceDropdownOpen(false);
                    navigate('/workspace-settings');
                  }}
                  className="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-popover-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted rounded-lg transition-all duration-200 border border-transparent hover:border-border"
                >
                  <div className="w-6 h-6 bg-muted rounded-md flex items-center justify-center">
                    <Settings className="w-3.5 h-3.5 text-muted-foreground" />
                  </div>
                  Workspace Settings
                </button>
              </div>
              <div className="relative mt-1">
                <button
                  onClick={() => {
                    setWorkspaceDropdownOpen(false);
                    navigate('/account-settings');
                  }}
                  className="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-popover-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted rounded-lg transition-all duration-200 border border-transparent hover:border-border"
                >
                  <div className="w-6 h-6 bg-muted rounded-md flex items-center justify-center">
                    <Settings className="w-3.5 h-3.5 text-muted-foreground" />
                  </div>
                  Account Settings
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create Workspace Modal */}
      <CreateWorkspaceModal
        isOpen={createWorkspaceModalOpen}
        onClose={() => setCreateWorkspaceModalOpen(false)}
      />
    </>
  );
};

export default WorkspaceDropdown;
